import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI } from '../../services/api';
import {
    Activity,
    TrendingUp,
    Users,
    BarChart3,
    Zap,
    DollarSign,
    Target,
    Award,
    Trophy,
    Heart,
    GitCompare,
    UserPlus,
    Package,
    ShoppingCart,
    Truck,
    Beaker,
    LineChart,
    Brain,
    FileText,
    Download,
    ChevronRight,
    Star,
    ArrowUp,
    ArrowDown,
} from 'lucide-react';
import { ChartBarIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

interface EnhancedSectionsProps {
    period?: string;
    startDate?: string;
    endDate?: string;
    isDemoMode?: boolean;
    overview?: any;
    salesmanPerformance?: any[];
    dealsByPeriod?: any[];
    clientsByPeriod?: any[];
    revenueTrends?: any[];
    topPerformers?: any;
    activitySummary?: any;
    stockAnalytics?: any;
    productAnalytics?: any;
    supplierAnalytics?: any;
}

// ============= RICH VISUALIZATION COMPONENTS =============

// ===== 1. Real-time Activity - Animated Orbital Activity Monitor =====
const ActivityRingsVisualization: React.FC<{ isDemoMode?: boolean; data?: any }> = ({ isDemoMode = true, data }) => {
    // Mock data for demo mode
    const mockActivities = [
        { label: 'Deals', value: 45, max: 60, color: '#3B82F6', angle: 0 },
        { label: 'Leads', value: 36, max: 50, color: '#10B981', angle: 90 },
        { label: 'Meetings', value: 12, max: 15, color: '#F59E0B', angle: 180 },
        { label: 'Calls', value: 28, max: 40, color: '#8B5CF6', angle: 270 },
    ];

    // Use live data if available and not in demo mode
    const activities = (!isDemoMode && data) ? [
        { label: 'Deals', value: data.totalDeals || 0, max: 60, color: '#3B82F6', angle: 0 },
        { label: 'Leads', value: data.totalLeads || 0, max: 50, color: '#10B981', angle: 90 },
        { label: 'Meetings', value: data.totalMeetings || 0, max: 15, color: '#F59E0B', angle: 180 },
        { label: 'Calls', value: data.totalCalls || 0, max: 40, color: '#8B5CF6', angle: 270 },
    ] : mockActivities;

    const totalValue = activities.reduce((sum, a) => sum + a.value, 0);
    const avgPercentage = activities.reduce((sum, a) => sum + (a.value / a.max), 0) / activities.length * 100;

    return (
        <div className="py-4">
            <div className="relative flex justify-center" style={{ height: 220 }}>
                {/* Outer rotating ring */}
                <motion.div
                    className="absolute inset-0 flex items-center justify-center"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                >
                    <svg width="200" height="200" viewBox="0 0 200 200" className="opacity-20">
                        <circle cx="100" cy="100" r="90" fill="none" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4 4" />
                    </svg>
                </motion.div>

                {/* Inner pulse rings */}
                {[60, 75, 90].map((r, i) => (
                    <motion.div
                        key={r}
                        className="absolute inset-0 flex items-center justify-center"
                        initial={{ opacity: 0.3 }}
                        animate={{ opacity: [0.1, 0.3, 0.1] }}
                        transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                    >
                        <svg width="200" height="200" viewBox="0 0 200 200">
                            <circle cx="100" cy="100" r={r} fill="none" stroke="#3B82F6" strokeWidth="1" opacity="0.3" />
                        </svg>
                    </motion.div>
                ))}

                {/* Activity orbs orbiting */}
                {activities.map((activity, index) => {
                    const orbitRadius = 70;
                    const percentage = (activity.value / activity.max) * 100;
                    const orbSize = 16 + (percentage / 100) * 12;

                    return (
                        <motion.div
                            key={activity.label}
                            className="absolute"
                            style={{
                                width: 200,
                                height: 200,
                                left: '50%',
                                top: '50%',
                                marginLeft: -100,
                                marginTop: -100,
                            }}
                            animate={{ rotate: [activity.angle, activity.angle + 360] }}
                            transition={{ duration: 20 + index * 2, repeat: Infinity, ease: 'linear' }}
                        >
                            <motion.div
                                className="absolute rounded-full flex items-center justify-center shadow-lg"
                                style={{
                                    width: orbSize,
                                    height: orbSize,
                                    backgroundColor: activity.color,
                                    left: 100 - orbSize / 2,
                                    top: 100 - orbitRadius - orbSize / 2,
                                    boxShadow: `0 0 20px ${activity.color}60`,
                                }}
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ duration: 2, repeat: Infinity, delay: index * 0.25 }}
                            >
                                <span className="text-white text-xs font-bold">{activity.value}</span>
                            </motion.div>
                            {/* Trailing particle */}
                            <motion.div
                                className="absolute rounded-full"
                                style={{
                                    width: 6,
                                    height: 6,
                                    backgroundColor: activity.color,
                                    opacity: 0.4,
                                    left: 97,
                                    top: 100 - orbitRadius + 15,
                                }}
                            />
                        </motion.div>
                    );
                })}

                {/* Central hub */}
                <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                        className="w-24 h-24 rounded-full bg-gradient-to-br from-slate-900 to-slate-700 flex flex-col items-center justify-center shadow-2xl"
                        animate={{ boxShadow: ['0 0 30px rgba(59,130,246,0.3)', '0 0 50px rgba(59,130,246,0.5)', '0 0 30px rgba(59,130,246,0.3)'] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        <motion.span
                            className="text-2xl font-bold text-white"
                            animate={{ scale: [1, 1.05, 1] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                        >
                            {totalValue}
                        </motion.span>
                        <span className="text-xs text-gray-400">Active</span>
                        <motion.div
                            className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"
                            animate={{ scale: [1, 1.3, 1] }}
                            transition={{ duration: 1, repeat: Infinity }}
                        />
                    </motion.div>
                </div>
            </div>

            {/* Legend with animated bars */}
            <div className="grid grid-cols-4 gap-2 mt-4">
                {activities.map((activity, index) => (
                    <motion.div
                        key={activity.label}
                        className="text-center"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                    >
                        <div className="w-2 h-2 rounded-full mx-auto mb-1" style={{ backgroundColor: activity.color }} />
                        <div className="text-xs text-gray-600">{activity.label}</div>
                        <div className="text-sm font-bold" style={{ color: activity.color }}>{Math.round((activity.value / activity.max) * 100)}%</div>
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

// ===== 2. Revenue Trends - Animated Wave Chart =====
const RevenueWaveVisualization: React.FC<{ isDemoMode?: boolean; data?: any[] }> = ({ isDemoMode = true, data: liveData }) => {
    const mockData = [
        { month: 'Jan', value: 65 },
        { month: 'Feb', value: 72 },
        { month: 'Mar', value: 78 },
        { month: 'Apr', value: 85 },
        { month: 'May', value: 92 },
        { month: 'Jun', value: 88 },
    ];

    // Use live data if available and not in demo mode
    const data = (!isDemoMode && liveData && liveData.length > 0)
        ? liveData.slice(0, 6).map((item: any, index: number) => ({
            month: item.period || item.month || `M${index + 1}`,
            value: parseFloat(item.revenue || item.value || 0)
        }))
        : mockData;

    const maxValue = Math.max(...data.map(d => d.value));

    const points = data.map((d, i) => ({
        x: (i / (data.length - 1)) * 280 + 20,
        y: 120 - (d.value / maxValue) * 80,
    }));

    const pathD = `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`;
    const areaD = `${pathD} L ${points[points.length - 1].x},120 L ${points[0].x},120 Z`;

    return (
        <div className="py-4">
            <div className="flex justify-between mb-2">
                <span className="text-2xl font-bold text-gray-900">$892K</span>
                <span className="flex items-center gap-1 text-green-500 text-sm font-medium">
                    <ArrowUp size={14} /> +18.5%
                </span>
            </div>
            <svg width="100%" height="140" viewBox="0 0 320 140" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10B981" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <motion.path
                    d={areaD}
                    fill="url(#revenueGradient)"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                />
                <motion.path
                    d={pathD}
                    fill="none"
                    stroke="#10B981"
                    strokeWidth="3"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.5 }}
                />
                {points.map((point, i) => (
                    <motion.circle
                        key={i}
                        cx={point.x}
                        cy={point.y}
                        r="5"
                        fill="#10B981"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.8 + i * 0.1 }}
                    />
                ))}
                {data.map((d, i) => (
                    <text key={i} x={points[i].x} y="135" textAnchor="middle" className="text-xs fill-gray-400">
                        {d.month}
                    </text>
                ))}
            </svg>
        </div>
    );
};

// ===== 3. Team Performance - Concentric Circular Progress Rings =====
const TeamPerformanceVisualization: React.FC<{ isDemoMode?: boolean; data?: any[] }> = ({ isDemoMode = true, data: liveData }) => {
    const mockPerformers = [
        { name: 'John S.', score: 92, revenue: '$145K', deals: 23, color: '#3B82F6' },
        { name: 'Sarah J.', score: 88, revenue: '$132K', deals: 21, color: '#10B981' },
        { name: 'Lisa A.', score: 85, revenue: '$118K', deals: 19, color: '#F59E0B' },
        { name: 'Emily B.', score: 82, revenue: '$105K', deals: 17, color: '#8B5CF6' },
        { name: 'Mike D.', score: 78, revenue: '$98K', deals: 15, color: '#EC4899' },
        { name: 'Alex W.', score: 75, revenue: '$87K', deals: 14, color: '#14B8A6' },
    ];

    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6'];

    // Use live data if available and not in demo mode
    const performers = (!isDemoMode && liveData && liveData.length > 0)
        ? liveData.slice(0, 6).map((item: any, index: number) => ({
            name: item.name || item.salesmanName || 'Unknown',
            score: Math.min(100, Math.round((parseFloat(item.totalRevenue || 0) / 10000) || 75)),
            revenue: `$${(parseFloat(item.totalRevenue || 0) / 1000).toFixed(0)}K`,
            deals: item.dealsCount || item.totalDeals || 0,
            color: colors[index % colors.length]
        }))
        : mockPerformers;

    const sortedPerformers = [...performers].sort((a, b) => b.score - a.score);
    const avgScore = Math.round(performers.reduce((sum, p) => sum + p.score, 0) / performers.length);

    // Ring configuration
    const centerX = 120;
    const centerY = 120;
    const ringWidth = 12;
    const ringGap = 4;
    const startRadius = 100;

    return (
        <div className="py-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <motion.div
                        className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg"
                        animate={{ rotate: [0, 5, -5, 0] }}
                        transition={{ duration: 4, repeat: Infinity }}
                    >
                        <Users className="w-5 h-5 text-white" />
                    </motion.div>
                    <div>
                        <span className="text-lg font-bold text-gray-900">{performers.length} Members</span>
                        <div className="text-xs text-gray-500">Active team</div>
                    </div>
                </div>
            </div>

            {/* Concentric Rings Visualization */}
            <div className="flex items-center justify-center gap-6">
                <div className="relative" style={{ width: 240, height: 240 }}>
                    <svg width="240" height="240" viewBox="0 0 240 240">
                        {sortedPerformers.map((performer, index) => {
                            const radius = startRadius - index * (ringWidth + ringGap);
                            const circumference = 2 * Math.PI * radius;
                            const progress = (performer.score / 100) * circumference;
                            const dashOffset = circumference - progress;

                            return (
                                <g key={performer.name}>
                                    {/* Background ring */}
                                    <circle
                                        cx={centerX}
                                        cy={centerY}
                                        r={radius}
                                        fill="none"
                                        stroke="#f1f5f9"
                                        strokeWidth={ringWidth}
                                    />
                                    {/* Progress ring */}
                                    <motion.circle
                                        cx={centerX}
                                        cy={centerY}
                                        r={radius}
                                        fill="none"
                                        stroke={performer.color}
                                        strokeWidth={ringWidth}
                                        strokeLinecap="round"
                                        strokeDasharray={circumference}
                                        initial={{ strokeDashoffset: circumference }}
                                        animate={{ strokeDashoffset: dashOffset }}
                                        transition={{ duration: 1.2, delay: index * 0.15, ease: 'easeOut' }}
                                        style={{
                                            transform: 'rotate(-90deg)',
                                            transformOrigin: 'center',
                                            filter: `drop-shadow(0 0 6px ${performer.color}50)`,
                                        }}
                                    />
                                    {/* Glow effect */}
                                    <motion.circle
                                        cx={centerX}
                                        cy={centerY}
                                        r={radius}
                                        fill="none"
                                        stroke={performer.color}
                                        strokeWidth={ringWidth + 4}
                                        strokeLinecap="round"
                                        strokeDasharray={circumference}
                                        strokeDashoffset={dashOffset}
                                        opacity={0.15}
                                        style={{
                                            transform: 'rotate(-90deg)',
                                            transformOrigin: 'center',
                                        }}
                                        initial={{ strokeDashoffset: circumference }}
                                        animate={{ strokeDashoffset: dashOffset }}
                                        transition={{ duration: 1.2, delay: index * 0.15, ease: 'easeOut' }}
                                    />
                                </g>
                            );
                        })}
                    </svg>

                    {/* Center content */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <motion.div
                            className="text-center bg-white rounded-full p-4 shadow-lg"
                            style={{ width: 80, height: 80 }}
                            animate={{ scale: [1, 1.03, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                {avgScore}%
                            </div>
                            <div className="text-xs text-gray-500">Avg</div>
                        </motion.div>
                    </div>
                </div>

                {/* Legend */}
                <div className="space-y-2">
                    {sortedPerformers.map((performer, index) => (
                        <motion.div
                            key={performer.name}
                            className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.5 + index * 0.1 }}
                            whileHover={{ scale: 1.02 }}
                        >
                            <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{
                                    backgroundColor: performer.color,
                                    boxShadow: `0 0 8px ${performer.color}60`
                                }}
                            />
                            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">{performer.name}</span>
                            <span
                                className="text-xs font-bold ml-auto"
                                style={{ color: performer.color }}
                            >
                                {performer.score}%
                            </span>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ===== 4. Activity Summary - Animated Stats Grid =====
const ActivitySummaryVisualization: React.FC<{ isDemoMode?: boolean; data?: any }> = ({ isDemoMode = true, data: liveData }) => {
    const mockStats = [
        { label: 'Deals Closed', value: 164, change: +12, icon: Target },
        { label: 'Revenue', value: '$892K', change: +18, icon: DollarSign },
        { label: 'Clients', value: 156, change: +8, icon: Users },
        { label: 'Tasks Done', value: 342, change: +24, icon: Activity },
    ];

    // Use live data if available and not in demo mode
    const stats = (!isDemoMode && liveData)
        ? [
            { label: 'Deals Closed', value: liveData.totalDeals || 0, change: +12, icon: Target },
            { label: 'Revenue', value: `$${(parseFloat(liveData.totalRevenue || 0) / 1000).toFixed(0)}K`, change: +18, icon: DollarSign },
            { label: 'Clients', value: liveData.totalClients || 0, change: +8, icon: Users },
            { label: 'Tasks Done', value: liveData.completedActivities || 342, change: +24, icon: Activity },
        ]
        : mockStats;

    return (
        <div className="grid grid-cols-2 gap-3 py-4">
            {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                    <motion.div
                        key={stat.label}
                        className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-xl border border-gray-100"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <Icon size={16} className="text-gray-400" />
                            <span className="text-xs text-gray-500">{stat.label}</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className="text-xl font-bold text-gray-900">{stat.value}</span>
                            <span className="text-xs text-green-500 flex items-center">
                                <ArrowUp size={10} />+{stat.change}%
                            </span>
                        </div>
                    </motion.div>
                );
            })}
        </div>
    );
};

// ===== 5. Top Performers - Podium Visualization with Realistic Metallic Colors =====
const TopPerformersVisualization: React.FC<{ isDemoMode?: boolean; data?: any; period?: string; startDate?: string; endDate?: string }> = ({
    isDemoMode = true,
    data: liveData,
    period = '30d',
    startDate,
    endDate
}) => {
    const [performers, setPerformers] = React.useState<any[]>([
        { name: 'Sarah J.', score: 88, rank: 2, revenue: '$420K' },
        { name: 'John S.', score: 92, rank: 1, revenue: '$450K' },
        { name: 'Mike D.', score: 75, rank: 3, revenue: '$280K' },
    ]);

    React.useEffect(() => {
        const fetchTopPerformers = async () => {
            console.log('🏆 TopPerformers - useEffect triggered - isDemoMode:', isDemoMode, 'period:', period);

            if (isDemoMode) {
                // Use mock data in demo mode
                console.log('🏆 TopPerformers - Using DEMO mode data');
                setPerformers([
                    { name: 'Sarah J.', score: 88, rank: 2, revenue: '$420K' },
                    { name: 'John S.', score: 92, rank: 1, revenue: '$450K' },
                    { name: 'Mike D.', score: 75, rank: 3, revenue: '$280K' },
                ]);
                return;
            }

            console.log('🏆 TopPerformers - Fetching LIVE data...');
            try {
                // Fetch real data from backend
                const response = await analyticsAPI.getTopPerformers(period, startDate, endDate);
                console.log('🏆 TopPerformers - Raw API response:', JSON.stringify(response, null, 2));

                if (response && response.topByRevenue && response.topByRevenue.length > 0) {
                    console.log('🏆 TopPerformers - Processing', response.topByRevenue.length, 'performers');
                    const livePerformers = response.topByRevenue.slice(0, 3).map((item: any, index: number) => {
                        const firstName = item.name?.split(' ')[0] || item.email?.split('@')[0] || 'User';
                        const lastInitial = item.name?.split(' ')[1]?.[0] || '';
                        const performer = {
                            name: `${firstName} ${lastInitial}.`,
                            score: 92 - (index * 8), // Decreasing scores for visual effect
                            rank: index + 1,
                            revenue: `$${(parseFloat(item.totalRevenue || 0) / 1000).toFixed(0)}K`
                        };
                        console.log('🏆 TopPerformers - Mapped performer:', performer, 'from:', item);
                        return performer;
                    });
                    console.log('🏆 TopPerformers - Setting live performers:', livePerformers);
                    setPerformers(livePerformers);
                } else {
                    console.warn('🏆 TopPerformers - No data in response, keeping current state');
                    console.log('🏆 TopPerformers - Response structure:', response);
                }
            } catch (error) {
                console.error('🏆 TopPerformers - Error fetching data:', error);
                console.error('🏆 TopPerformers - Error details:', error instanceof Error ? error.message : String(error));
            }
        };

        fetchTopPerformers();
    }, [isDemoMode, period, startDate, endDate]);

    console.log('🏆 TopPerformers - isDemoMode:', isDemoMode, 'performers:', performers);

    const heights = [80, 110, 60];

    // Realistic metallic gradients with lighting effects
    const metallicGradients = [
        // Silver (2nd place)
        {
            main: 'linear-gradient(180deg, #F0F0F0 0%, #E8E8E8 15%, #D4D4D4 30%, #C0C0C0 50%, #A8A8A8 70%, #989898 85%, #888888 100%)',
            shine: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.4) 25%, transparent 50%, rgba(255,255,255,0.2) 75%, rgba(255,255,255,0.6) 100%)',
            shadow: '0 8px 32px rgba(128, 128, 128, 0.5), inset 0 2px 4px rgba(255, 255, 255, 0.8), inset 0 -2px 4px rgba(0, 0, 0, 0.2)',
            textShadow: '0 1px 2px rgba(0,0,0,0.3), 0 0 20px rgba(255,255,255,0.5)',
            avatarBg: 'linear-gradient(135deg, #E8E8E8 0%, #C0C0C0 50%, #A0A0A0 100%)',
        },
        // Gold (1st place)
        {
            main: 'linear-gradient(180deg, #FFF8DC 0%, #FFE55C 10%, #FFD700 25%, #DAA520 50%, #B8860B 70%, #CD853F 85%, #8B7355 100%)',
            shine: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,248,220,0.6) 20%, transparent 45%, rgba(255,215,0,0.3) 70%, rgba(255,255,255,0.7) 100%)',
            shadow: '0 8px 32px rgba(218, 165, 32, 0.6), 0 0 60px rgba(255, 215, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.9), inset 0 -2px 4px rgba(139, 69, 19, 0.3)',
            textShadow: '0 1px 3px rgba(139, 69, 19, 0.5), 0 0 30px rgba(255, 215, 0, 0.8)',
            avatarBg: 'linear-gradient(135deg, #FFE55C 0%, #FFD700 50%, #DAA520 100%)',
        },
        // Bronze (3rd place)
        {
            main: 'linear-gradient(180deg, #E6C9A8 0%, #D4A574 15%, #CD7F32 30%, #B87333 50%, #A0522D 70%, #8B4513 85%, #654321 100%)',
            shine: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(230,201,168,0.5) 25%, transparent 50%, rgba(205,127,50,0.3) 75%, rgba(255,255,255,0.5) 100%)',
            shadow: '0 8px 32px rgba(139, 69, 19, 0.5), inset 0 2px 4px rgba(255, 255, 255, 0.7), inset 0 -2px 4px rgba(101, 67, 33, 0.4)',
            textShadow: '0 1px 2px rgba(101, 67, 33, 0.5), 0 0 15px rgba(205, 127, 50, 0.5)',
            avatarBg: 'linear-gradient(135deg, #D4A574 0%, #CD7F32 50%, #B87333 100%)',
        },
    ];

    return (
        <div className="py-4">
            <div className="flex items-end justify-center gap-4 h-48">
                {performers.map((p: any, index: number) => (
                    <motion.div
                        key={p.name}
                        className="flex flex-col items-center"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.2, type: 'spring' }}
                    >
                        {/* Avatar with metallic ring */}
                        <motion.div
                            className="relative w-16 h-16 rounded-full flex items-center justify-center mb-2"
                            style={{
                                background: metallicGradients[index].avatarBg,
                                boxShadow: `0 4px 15px rgba(0,0,0,0.2), inset 0 2px 3px rgba(255,255,255,0.5)`,
                            }}
                            whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
                            transition={{ duration: 0.3 }}
                        >
                            {/* Inner avatar */}
                            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-inner">
                                <span className="text-xl font-bold text-gray-700">{p.name.charAt(0)}</span>
                            </div>
                            {/* Crown for 1st place */}
                            {index === 1 && (
                                <motion.div
                                    className="absolute -top-3 left-1/2 transform -translate-x-1/2"
                                    animate={{ y: [0, -2, 0], rotate: [0, 5, -5, 0] }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                >
                                    <span className="text-2xl drop-shadow-lg">👑</span>
                                </motion.div>
                            )}
                        </motion.div>

                        <span className="text-xs font-medium text-gray-600 mb-0.5">{p.name}</span>
                        <span className="text-xs text-gray-400 mb-2">{p.revenue}</span>

                        {/* Metallic podium with realistic lighting */}
                        <motion.div
                            className="w-20 rounded-t-xl flex items-center justify-center relative overflow-hidden"
                            style={{
                                background: metallicGradients[index].main,
                                boxShadow: metallicGradients[index].shadow,
                                height: heights[index]
                            }}
                            initial={{ height: 0 }}
                            animate={{ height: heights[index] }}
                            transition={{ delay: 0.5 + index * 0.2, duration: 0.6, ease: 'easeOut' }}
                        >
                            {/* Shine overlay */}
                            <motion.div
                                className="absolute inset-0 pointer-events-none"
                                style={{ background: metallicGradients[index].shine }}
                            />

                            {/* Animated shine sweep */}
                            <motion.div
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                                    transform: 'translateX(-100%)',
                                }}
                                animate={{ transform: ['translateX(-100%)', 'translateX(100%)'] }}
                                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3, delay: index * 0.5 }}
                            />

                            {/* Edge highlights */}
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-white/40 via-white/20 to-transparent" />
                            <div className="absolute right-0 top-0 bottom-0 w-1 bg-gradient-to-b from-black/10 via-black/20 to-black/30" />

                            {/* Rank number */}
                            <motion.span
                                className="relative text-3xl font-extrabold text-white z-10"
                                style={{ textShadow: metallicGradients[index].textShadow }}
                                animate={{ scale: [1, 1.05, 1] }}
                                transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
                            >
                                {p.rank}
                            </motion.span>
                        </motion.div>
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

// ===== 6. Client Tracking - Client Status Bubbles =====
const ClientTrackingVisualization: React.FC<{ isDemoMode?: boolean; data?: any[]; period?: string; startDate?: string; endDate?: string }> = ({
    isDemoMode = true,
    data: liveData,
    period = '30d',
    startDate,
    endDate
}) => {
    const [clients, setClients] = React.useState<any[]>([
        { status: 'Active', count: 156, color: '#10B981' },
        { status: 'Pending', count: 24, color: '#F59E0B' },
        { status: 'New', count: 11, color: '#3B82F6' },
        { status: 'At Risk', count: 8, color: '#EF4444' },
    ]);

    React.useEffect(() => {
        const fetchClientAnalytics = async () => {
            if (isDemoMode) {
                // Use mock data in demo mode
                setClients([
                    { status: 'Active', count: 156, color: '#10B981' },
                    { status: 'Pending', count: 24, color: '#F59E0B' },
                    { status: 'New', count: 11, color: '#3B82F6' },
                    { status: 'At Risk', count: 8, color: '#EF4444' },
                ]);
                return;
            }

            try {
                // Fetch real data from backend
                const response = await analyticsAPI.getClientAnalytics(period, startDate, endDate);
                console.log('👥 ClientTracking - Fetched data:', response);

                if (response && response.statusBreakdown) {
                    // Map status breakdown to visualization format
                    const statusColors: Record<string, { status: string; color: string }> = {
                        'ACTIVE': { status: 'Active', color: '#10B981' },
                        'NEW': { status: 'New', color: '#3B82F6' },
                        'INACTIVE': { status: 'Inactive', color: '#EF4444' },
                        'VIP': { status: 'VIP', color: '#8B5CF6' },
                    };

                    const liveClients = Object.entries(response.statusBreakdown)
                        .filter(([_, count]) => (count as number) > 0)
                        .map(([status, count]) => ({
                            status: statusColors[status]?.status || status,
                            count: count as number,
                            color: statusColors[status]?.color || '#6B7280',
                        }));

                    setClients(liveClients.length > 0 ? liveClients : [
                        { status: 'No Data', count: 0, color: '#9CA3AF' }
                    ]);
                    console.log('👥 ClientTracking - Using live clients:', liveClients);
                } else {
                    console.log('👥 ClientTracking - No data, using mock');
                }
            } catch (error) {
                console.error('👥 ClientTracking - Error fetching data:', error);
            }
        };

        fetchClientAnalytics();
    }, [isDemoMode, period, startDate, endDate]);

    console.log('👥 ClientTracking - isDemoMode:', isDemoMode, 'clients:', clients);

    return (
        <div className="flex items-center justify-center gap-6 py-6">
            <div className="relative" style={{ width: 140, height: 140 }}>
                {clients.map((client, index) => {
                    const size = 36 + client.count / 4;
                    const angle = (index / clients.length) * 2 * Math.PI - Math.PI / 2;
                    const radius = 45;
                    const x = 70 + Math.cos(angle) * radius - size / 2;
                    const y = 70 + Math.sin(angle) * radius - size / 2;

                    return (
                        <motion.div
                            key={client.status}
                            className="absolute rounded-full flex items-center justify-center text-white font-bold shadow-lg"
                            style={{
                                width: size,
                                height: size,
                                left: x,
                                top: y,
                                backgroundColor: client.color,
                                fontSize: size / 3,
                            }}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: index * 0.15, type: 'spring' }}
                            whileHover={{ scale: 1.2, zIndex: 10 }}
                        >
                            {client.count}
                        </motion.div>
                    );
                })}
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{clients.reduce((sum, c) => sum + c.count, 0)}</div>
                        <div className="text-xs text-gray-500">Total</div>
                    </div>
                </div>
            </div>
            <div className="space-y-2">
                {clients.map((client, index) => (
                    <motion.div
                        key={client.status}
                        className="flex items-center gap-2"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                    >
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: client.color }} />
                        <span className="text-sm text-gray-600">{client.status}</span>
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

// ===== 7. Salesman Comparison - Radar Web with Selector =====
const SalesmanComparisonVisualization: React.FC<{ isDemoMode?: boolean; data?: any[] }> = ({ isDemoMode = true, data: liveData }) => {
    const metrics = ['Revenue', 'Clients', 'Deals', 'Growth', 'Retention'];
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6'];

    const mockSalesmen = [
        { id: 1, name: 'John Smith', values: [90, 85, 88, 75, 92], color: '#3B82F6' },
        { id: 2, name: 'Sarah Johnson', values: [85, 90, 82, 88, 85], color: '#10B981' },
        { id: 3, name: 'Mike Davis', values: [78, 72, 80, 92, 76], color: '#F59E0B' },
        { id: 4, name: 'Lisa Anderson', values: [88, 82, 95, 70, 88], color: '#8B5CF6' },
        { id: 5, name: 'Emily Brown', values: [75, 95, 72, 85, 90], color: '#EC4899' },
        { id: 6, name: 'Alex Wilson', values: [82, 78, 85, 80, 82], color: '#14B8A6' },
    ];

    // Use live data if available and not in demo mode
    const allSalesmen = (!isDemoMode && liveData && liveData.length > 0)
        ? liveData.slice(0, 6).map((item: any, index: number) => {
            const revenue = parseFloat(item.totalRevenue || 0);
            return {
                id: item.id || index + 1,
                name: item.name || item.salesmanName || 'Unknown',
                values: [
                    Math.min(100, Math.round(revenue / 10000) || 75),
                    Math.min(100, (item.clientsCount || 0) * 5),
                    Math.min(100, (item.dealsCount || 0) * 5),
                    Math.round(Math.random() * 30 + 70),
                    Math.round(Math.random() * 30 + 70),
                ],
                color: colors[index % colors.length]
            };
        })
        : mockSalesmen;

    const [selectedSalesman1, setSelectedSalesman1] = React.useState(allSalesmen[0]);
    const [selectedSalesman2, setSelectedSalesman2] = React.useState(allSalesmen[1]);
    const [showDropdown1, setShowDropdown1] = React.useState(false);
    const [showDropdown2, setShowDropdown2] = React.useState(false);

    const selectedSalesmen = [selectedSalesman1, selectedSalesman2];

    const angleStep = (2 * Math.PI) / metrics.length;
    const radius = 55;
    const center = 75;

    return (
        <div className="py-4">
            {/* Salesman Selectors */}
            <div className="flex gap-3 mb-4">
                {/* Selector 1 */}
                <div className="flex-1 relative">
                    <button
                        onClick={() => { setShowDropdown1(!showDropdown1); setShowDropdown2(false); }}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors text-left"
                    >
                        <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: selectedSalesman1.color }}
                        />
                        <span className="text-sm font-medium text-gray-700 truncate flex-1">{selectedSalesman1.name}</span>
                        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${showDropdown1 ? 'rotate-90' : ''}`} />
                    </button>
                    {showDropdown1 && (
                        <motion.div
                            className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg border border-gray-200 shadow-lg z-10 overflow-hidden"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            {allSalesmen.filter(s => s.id !== selectedSalesman2.id).map((salesman) => (
                                <button
                                    key={salesman.id}
                                    onClick={() => { setSelectedSalesman1(salesman); setShowDropdown1(false); }}
                                    className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 transition-colors text-left ${selectedSalesman1.id === salesman.id ? 'bg-blue-50' : ''}`}
                                >
                                    <div
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: salesman.color }}
                                    />
                                    <span className="text-sm text-gray-700">{salesman.name}</span>
                                </button>
                            ))}
                        </motion.div>
                    )}
                </div>

                <div className="flex items-center text-gray-400 text-sm font-medium">vs</div>

                {/* Selector 2 */}
                <div className="flex-1 relative">
                    <button
                        onClick={() => { setShowDropdown2(!showDropdown2); setShowDropdown1(false); }}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors text-left"
                    >
                        <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: selectedSalesman2.color }}
                        />
                        <span className="text-sm font-medium text-gray-700 truncate flex-1">{selectedSalesman2.name}</span>
                        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${showDropdown2 ? 'rotate-90' : ''}`} />
                    </button>
                    {showDropdown2 && (
                        <motion.div
                            className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg border border-gray-200 shadow-lg z-10 overflow-hidden"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            {allSalesmen.filter(s => s.id !== selectedSalesman1.id).map((salesman) => (
                                <button
                                    key={salesman.id}
                                    onClick={() => { setSelectedSalesman2(salesman); setShowDropdown2(false); }}
                                    className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 transition-colors text-left ${selectedSalesman2.id === salesman.id ? 'bg-green-50' : ''}`}
                                >
                                    <div
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: salesman.color }}
                                    />
                                    <span className="text-sm text-gray-700">{salesman.name}</span>
                                </button>
                            ))}
                        </motion.div>
                    )}
                </div>
            </div>

            {/* Radar Chart */}
            <div className="flex items-center justify-center">
                <svg width="150" height="150" viewBox="0 0 150 150">
                    {/* Grid circles */}
                    {[0.25, 0.5, 0.75, 1].map((scale, i) => (
                        <motion.circle
                            key={i}
                            cx={center}
                            cy={center}
                            r={radius * scale}
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="1"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: i * 0.1 }}
                        />
                    ))}

                    {/* Axis lines */}
                    {metrics.map((_, i) => {
                        const angle = i * angleStep - Math.PI / 2;
                        const x = center + Math.cos(angle) * radius;
                        const y = center + Math.sin(angle) * radius;
                        return (
                            <line key={i} x1={center} y1={center} x2={x} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                        );
                    })}

                    {/* Data polygons */}
                    {selectedSalesmen.map((salesman, si) => {
                        const points = salesman.values.map((v, i) => {
                            const angle = i * angleStep - Math.PI / 2;
                            const r = (v / 100) * radius;
                            return `${center + Math.cos(angle) * r},${center + Math.sin(angle) * r}`;
                        }).join(' ');

                        return (
                            <motion.polygon
                                key={`${salesman.id}-${si}`}
                                points={points}
                                fill={`${salesman.color}25`}
                                stroke={salesman.color}
                                strokeWidth="2"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.3 + si * 0.2 }}
                            />
                        );
                    })}

                    {/* Labels */}
                    {metrics.map((metric, i) => {
                        const angle = i * angleStep - Math.PI / 2;
                        const x = center + Math.cos(angle) * (radius + 14);
                        const y = center + Math.sin(angle) * (radius + 14);
                        return (
                            <text key={i} x={x} y={y} textAnchor="middle" dominantBaseline="middle" className="text-[10px] fill-gray-500">
                                {metric}
                            </text>
                        );
                    })}
                </svg>
            </div>

            {/* Legend with scores */}
            <div className="flex justify-center gap-6 mt-3">
                {selectedSalesmen.map((s, i) => (
                    <motion.div
                        key={s.id}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                        style={{ backgroundColor: `${s.color}10` }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6 + i * 0.1 }}
                    >
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                        <span className="text-xs font-medium" style={{ color: s.color }}>{s.name.split(' ')[0]}</span>
                        <span className="text-xs text-gray-500">
                            Avg: {Math.round(s.values.reduce((a, b) => a + b, 0) / s.values.length)}%
                        </span>
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

// ===== 8. Client Acquisition - Funnel Visualization =====
const ClientAcquisitionVisualization: React.FC<{ isDemoMode?: boolean; data?: any[] }> = ({ isDemoMode = true, data: liveData }) => {
    const mockStages = [
        { label: 'Leads', value: 1200, color: '#3B82F6' },
        { label: 'Qualified', value: 900, color: '#10B981' },
        { label: 'Negotiating', value: 600, color: '#F59E0B' },
        { label: 'Closed', value: 360, color: '#8B5CF6' },
    ];

    // Use live data if available and not in demo mode
    const totalClients = (!isDemoMode && liveData && liveData.length > 0)
        ? liveData.reduce((sum: number, item: any) => sum + (item.count || 0), 0)
        : 1200;

    const stages = (!isDemoMode && liveData && liveData.length > 0)
        ? [
            { label: 'Leads', value: totalClients * 1.5, color: '#3B82F6' },
            { label: 'Qualified', value: totalClients * 1.2, color: '#10B981' },
            { label: 'Negotiating', value: totalClients * 0.8, color: '#F59E0B' },
            { label: 'Closed', value: totalClients, color: '#8B5CF6' },
        ]
        : mockStages;

    const maxValue = stages[0].value;

    return (
        <div className="py-4 space-y-2">
            {stages.map((stage, index) => {
                const width = (stage.value / maxValue) * 100;
                return (
                    <motion.div
                        key={stage.label}
                        className="flex items-center gap-3"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.15 }}
                    >
                        <span className="w-20 text-sm text-gray-600 flex-shrink-0">{stage.label}</span>
                        <div className="flex-1 relative h-8">
                            <motion.div
                                className="h-full rounded-r-full relative overflow-hidden"
                                style={{
                                    backgroundColor: stage.color,
                                    clipPath: 'polygon(0 0, 100% 0, 95% 100%, 0 100%)'
                                }}
                                initial={{ width: 0 }}
                                animate={{ width: `${width}%` }}
                                transition={{ duration: 0.8, delay: index * 0.2 }}
                            >
                                {/* Shine effect */}
                                <div
                                    className="absolute inset-0 pointer-events-none"
                                    style={{
                                        background: 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)'
                                    }}
                                />
                            </motion.div>
                            {/* Value label - positioned outside the bar, to the left */}
                            <motion.span
                                className="absolute left-2 top-1/2 -translate-y-1/2 text-sm font-bold text-white drop-shadow-md"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.8 + index * 0.2 }}
                            >
                                {stage.value.toLocaleString()}
                            </motion.span>
                        </div>
                    </motion.div>
                );
            })}
            <div className="text-center mt-4 pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-500">Conversion Rate: </span>
                <motion.span
                    className="text-lg font-bold text-green-600"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                >
                    30%
                </motion.span>
            </div>
        </div>
    );
};

// ===== 9. Stock Analytics - Inventory Tanks =====
const StockAnalyticsVisualization: React.FC<{ isDemoMode?: boolean; data?: any }> = ({ isDemoMode = true, data: liveData }) => {
    const mockTanks = [
        { label: 'Products', level: 75, color: '#10B981' },
        { label: 'Raw Materials', level: 45, color: '#F59E0B' },
        { label: 'Packaging', level: 88, color: '#3B82F6' },
    ];

    // Use live data if available and not in demo mode
    const tanks = (!isDemoMode && liveData)
        ? [
            { label: 'Products', level: Math.min(100, (liveData.totalProducts || 0) / 2), color: '#10B981' },
            { label: 'Low Stock', level: Math.min(100, (liveData.lowStockItems || 0) * 4), color: '#F59E0B' },
            { label: 'Out of Stock', level: Math.min(100, (liveData.outOfStockItems || 0) * 10), color: '#EF4444' },
        ]
        : mockTanks;

    return (
        <div className="flex justify-center gap-8 py-6">
            {tanks.map((tank, index) => (
                <motion.div
                    key={tank.label}
                    className="flex flex-col items-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.15 }}
                >
                    <div className="relative w-16 h-24 rounded-lg border-2 border-gray-200 overflow-hidden bg-gray-50">
                        <motion.div
                            className="absolute bottom-0 left-0 right-0 rounded-b-md"
                            style={{ backgroundColor: tank.color }}
                            initial={{ height: 0 }}
                            animate={{ height: `${tank.level}%` }}
                            transition={{ duration: 1, delay: 0.3 + index * 0.2 }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-lg font-bold text-gray-700 drop-shadow">{tank.level}%</span>
                        </div>
                    </div>
                    <span className="text-xs text-gray-500 mt-2 text-center">{tank.label}</span>
                </motion.div>
            ))}
        </div>
    );
};

// ===== 10. Product Performance - Heat Grid =====
const ProductPerformanceVisualization: React.FC<{ isDemoMode?: boolean; data?: any }> = ({ isDemoMode = true, data: liveData }) => {
    const mockProducts = [
        { name: 'Widget Pro', sales: 92, growth: 15 },
        { name: 'Standard', sales: 78, growth: 8 },
        { name: 'Budget', sales: 65, growth: -2 },
        { name: 'Enterprise', sales: 88, growth: 22 },
    ];

    // Use live data if available and not in demo mode
    const products = (!isDemoMode && liveData && liveData.topProducts && liveData.topProducts.length > 0)
        ? liveData.topProducts.slice(0, 4).map((item: any) => ({
            name: item.name || item.productName || 'Unknown',
            sales: Math.min(100, Math.round((parseFloat(item.revenue || 0) / 10000) || 50)),
            growth: item.growth || Math.round(Math.random() * 30 - 5)
        }))
        : mockProducts;

    return (
        <div className="py-4 space-y-3">
            {products.map((product: any, index: number) => (
                <motion.div
                    key={product.name}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                >
                    <span className="w-20 text-sm text-gray-600 truncate">{product.name}</span>
                    <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden relative">
                        <motion.div
                            className="h-full rounded-lg"
                            style={{
                                backgroundColor: product.sales > 80 ? '#10B981' : product.sales > 60 ? '#F59E0B' : '#EF4444',
                            }}
                            initial={{ width: 0 }}
                            animate={{ width: `${product.sales}%` }}
                            transition={{ duration: 0.8, delay: index * 0.15 }}
                        />
                        <div className="absolute inset-0 flex items-center justify-between px-3">
                            <span className="text-xs font-medium text-white drop-shadow">{product.sales}%</span>
                            <span className={`text-xs font-medium ${product.growth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {product.growth >= 0 ? '+' : ''}{product.growth}%
                            </span>
                        </div>
                    </div>
                </motion.div>
            ))}
        </div>
    );
};

// ===== 11. Supplier Analytics - Score Cards =====
const SupplierAnalyticsVisualization: React.FC<{ isDemoMode?: boolean; data?: any }> = ({ isDemoMode = true, data: liveData }) => {
    const mockSuppliers = [
        { name: 'Supplier A', onTime: 92, quality: 95, icon: '🏆' },
        { name: 'Supplier B', onTime: 78, quality: 82, icon: '⭐' },
        { name: 'Supplier C', onTime: 85, quality: 88, icon: '✨' },
    ];

    const icons = ['🏆', '⭐', '✨', '💎', '🎯'];

    // Use live data if available and not in demo mode
    const suppliers = (!isDemoMode && liveData && liveData.topSuppliers && liveData.topSuppliers.length > 0)
        ? liveData.topSuppliers.slice(0, 3).map((item: any, index: number) => ({
            name: item.name || item.supplierName || 'Unknown',
            onTime: item.onTimeDelivery || Math.round(Math.random() * 30 + 70),
            quality: item.qualityScore || Math.round(Math.random() * 20 + 80),
            icon: icons[index % icons.length]
        }))
        : mockSuppliers;

    return (
        <div className="py-4 space-y-3">
            {suppliers.map((supplier: any, index: number) => (
                <motion.div
                    key={supplier.name}
                    className="flex items-center gap-4 p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.15 }}
                    whileHover={{ scale: 1.02 }}
                >
                    <span className="text-2xl">{supplier.icon}</span>
                    <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">{supplier.name}</span>
                        <div className="flex gap-4 mt-1">
                            <span className="text-xs text-gray-500">On-time: <strong className="text-green-600">{supplier.onTime}%</strong></span>
                            <span className="text-xs text-gray-500">Quality: <strong className="text-blue-600">{supplier.quality}%</strong></span>
                        </div>
                    </div>
                </motion.div>
            ))}
        </div>
    );
};

// ===== 12. Samples Tracking - Journey Timeline =====
const SamplesTrackingVisualization: React.FC<{ isDemoMode?: boolean; data?: any }> = ({ isDemoMode = true, data: liveData }) => {
    const mockStages = [
        { label: 'Sent', count: 140, completed: true },
        { label: 'Received', count: 120, completed: true },
        { label: 'Testing', count: 45, completed: false },
        { label: 'Approved', count: 30, completed: false },
    ];

    // Use live data if available and not in demo mode (for now, use mock data)
    const stages = mockStages;

    return (
        <div className="py-6">
            <div className="relative flex justify-between items-center">
                {/* Progress line */}
                <div className="absolute top-4 left-4 right-4 h-1 bg-gray-200 rounded-full">
                    <motion.div
                        className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: '50%' }}
                        transition={{ duration: 1 }}
                    />
                </div>

                {stages.map((stage, index) => (
                    <motion.div
                        key={stage.label}
                        className="relative flex flex-col items-center z-10"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.2 }}
                    >
                        <motion.div
                            className={`w-8 h-8 rounded-full flex items-center justify-center ${stage.completed ? 'bg-green-500' : 'bg-gray-300'
                                } shadow-lg`}
                            whileHover={{ scale: 1.2 }}
                        >
                            {stage.completed ? (
                                <span className="text-white text-sm">✓</span>
                            ) : (
                                <span className="text-white text-xs">{index + 1}</span>
                            )}
                        </motion.div>
                        <span className="text-xs text-gray-600 mt-2">{stage.label}</span>
                        <span className="text-sm font-bold text-gray-900">{stage.count}</span>
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

// ===== 13. Advanced Charts - Custom Chart Builder =====
const AdvancedChartsVisualization: React.FC<{ isDemoMode?: boolean; data?: any; period?: string; startDate?: string; endDate?: string }> = ({
    isDemoMode = true,
    data: liveData,
    period = '30d',
    startDate,
    endDate
}) => {
    console.log('📈 AdvancedCharts - isDemoMode:', isDemoMode, 'period:', period);

    const [showModal, setShowModal] = React.useState(false);
    const [chartConfig, setChartConfig] = React.useState({
        chartType: 'line' as 'line' | 'bar' | 'area',
        xAxisType: 'time' as 'time' | 'salesmen' | 'products' | 'clients' | 'regions',
        xAxisGranularity: 'day' as 'day' | 'week' | 'month' | 'quarter',
        yAxisMetric: 'revenue' as 'revenue' | 'orders' | 'performance' | 'clients' | 'deals' | 'growth',
        timeRange: '30d' as '7d' | '30d' | '90d' | '1y' | 'custom',
        compareBy: [] as string[],
    });
    const [generatedChart, setGeneratedChart] = React.useState<any>(null);
    const [isGenerating, setIsGenerating] = React.useState(false);
    const [realSalesmen, setRealSalesmen] = React.useState<string[]>([]);

    // Fetch real salespeople when component mounts
    React.useEffect(() => {
        const fetchSalespeople = async () => {
            if (!isDemoMode) {
                try {
                    // Fetch from the salesmanPerformance endpoint which returns salespeople data
                    const response = await analyticsAPI.getSalesmanPerformance(period, startDate, endDate);
                    console.log('📈 AdvancedCharts - Fetched salespeople:', response);

                    // Extract salesperson names from the response
                    const names = response.map((s: any) => s.salesmanName || s.name || 'Unknown');
                    setRealSalesmen(names);
                } catch (error) {
                    console.error('📈 AdvancedCharts - Error fetching salespeople:', error);
                    // Fallback to dummy data if fetch fails
                    setRealSalesmen(['John Smith', 'Sarah Johnson', 'Mike Davis', 'Lisa Anderson', 'Emily Brown', 'Alex Wilson']);
                }
            }
        };

        fetchSalespeople();
    }, [isDemoMode, period, startDate, endDate]);

    // Dummy data options (used in demo mode or as fallback)
    const categoryOptions = {
        salesmen: isDemoMode || realSalesmen.length === 0
            ? ['John Smith', 'Sarah Johnson', 'Mike Davis', 'Lisa Anderson', 'Emily Brown', 'Alex Wilson']
            : realSalesmen,
        products: ['Premium Widget', 'Standard Pack', 'Budget Line', 'Enterprise Suite', 'Starter Kit'],
        clients: ['Enterprise', 'SMB', 'Startup', 'Government', 'Non-Profit'],
        regions: ['North', 'South', 'East', 'West', 'Central'],
    };

    const metricLabels: Record<string, string> = {
        revenue: 'Revenue ($)',
        orders: 'Orders',
        performance: 'Performance (%)',
        clients: 'Clients',
        deals: 'Deals Closed',
        growth: 'Growth Rate (%)',
    };

    const chartTypeOptions = [
        { value: 'line', label: 'Line', icon: '📈' },
        { value: 'bar', label: 'Bar', icon: '📊' },
        { value: 'area', label: 'Area', icon: '📉' },
    ];

    const xAxisOptions = [
        { value: 'time', label: 'Time Period' },
        { value: 'salesmen', label: 'Salesmen' },
        { value: 'products', label: 'Products' },
        { value: 'clients', label: 'Client Segments' },
        { value: 'regions', label: 'Regions' },
    ];

    const yAxisOptions = [
        { value: 'revenue', label: 'Revenue ($)' },
        { value: 'orders', label: 'Orders Count' },
        { value: 'performance', label: 'Performance (%)' },
        { value: 'clients', label: 'Clients Count' },
        { value: 'deals', label: 'Deals Closed' },
        { value: 'growth', label: 'Growth Rate (%)' },
    ];

    const timeRangeOptions = [
        { value: '7d', label: '7 Days' },
        { value: '30d', label: '30 Days' },
        { value: '90d', label: '90 Days' },
        { value: '1y', label: '1 Year' },
    ];

    const granularityOptions = [
        { value: 'day', label: 'Daily' },
        { value: 'week', label: 'Weekly' },
        { value: 'month', label: 'Monthly' },
        { value: 'quarter', label: 'Quarterly' },
    ];

    // Generate chart data - fetch from backend in live mode, use dummy data in demo mode
    const generateChartData = async () => {
        const config = chartConfig;
        setIsGenerating(true);

        try {
            if (isDemoMode) {
                // Generate dummy data in demo mode
                let data: any[] = [];
                let series: string[] = [];

                if (config.xAxisType === 'time') {
                    const points = config.xAxisGranularity === 'day' ? 14 :
                        config.xAxisGranularity === 'week' ? 8 :
                            config.xAxisGranularity === 'month' ? 6 : 4;

                    const labels = config.xAxisGranularity === 'day'
                        ? Array.from({ length: points }, (_, i) => `Day ${i + 1}`)
                        : config.xAxisGranularity === 'week'
                            ? Array.from({ length: points }, (_, i) => `Week ${i + 1}`)
                            : config.xAxisGranularity === 'month'
                                ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']
                                : ['Q1', 'Q2', 'Q3', 'Q4'];

                    series = config.compareBy.length > 0 ? config.compareBy : ['Total'];

                    data = labels.map((label) => {
                        const point: any = { name: label };
                        series.forEach((s) => {
                            const baseValue = config.yAxisMetric === 'revenue' ? 50000 + Math.random() * 30000 :
                                config.yAxisMetric === 'orders' ? 100 + Math.random() * 150 :
                                    config.yAxisMetric === 'performance' ? 60 + Math.random() * 35 :
                                        config.yAxisMetric === 'clients' ? 20 + Math.random() * 40 :
                                            config.yAxisMetric === 'deals' ? 10 + Math.random() * 30 :
                                                -5 + Math.random() * 25;
                            point[s] = Math.round(baseValue * (0.8 + Math.random() * 0.4));
                        });
                        return point;
                    });
                } else {
                    const categories = categoryOptions[config.xAxisType as keyof typeof categoryOptions];
                    series = ['Value'];

                    data = categories.map((cat) => ({
                        name: cat,
                        Value: Math.round(
                            config.yAxisMetric === 'revenue' ? 30000 + Math.random() * 50000 :
                                config.yAxisMetric === 'orders' ? 50 + Math.random() * 200 :
                                    config.yAxisMetric === 'performance' ? 50 + Math.random() * 45 :
                                        config.yAxisMetric === 'clients' ? 10 + Math.random() * 50 :
                                            config.yAxisMetric === 'deals' ? 5 + Math.random() * 40 :
                                                -10 + Math.random() * 30
                        ),
                    }));
                }

                setGeneratedChart({ data, series, config: { ...config } });
            } else {
                // Fetch real data from backend
                console.log('📈 AdvancedCharts - Fetching LIVE data with config:', {
                    chartType: config.chartType,
                    xAxisType: config.xAxisType,
                    yAxisMetric: config.yAxisMetric,
                    timeRange: config.timeRange,
                    startDate,
                    endDate
                });

                try {
                    const response = await analyticsAPI.getAdvancedChartData(
                        config.chartType,
                        config.xAxisType === 'regions' ? 'clients' : config.xAxisType, // Map regions to clients for now
                        config.yAxisMetric,
                        config.timeRange,
                        startDate,
                        endDate
                    );

                    console.log('📈 AdvancedCharts - Raw API response:', JSON.stringify(response, null, 2));

                    if (response && response.data && response.data.length > 0) {
                        // Transform backend data to chart format
                        const chartData = response.data.map((item: any) => ({
                            name: item.label,
                            Value: item.value,
                            ...item
                        }));

                        console.log('📈 AdvancedCharts - Transformed chart data:', chartData);

                        setGeneratedChart({
                            data: chartData,
                            series: ['Value'],
                            config: { ...config }
                        });
                        console.log('📈 AdvancedCharts - Chart generated successfully!');
                    } else {
                        console.warn('📈 AdvancedCharts - No data in response or empty array');
                        console.log('📈 AdvancedCharts - Response:', response);
                        // Still set the chart with empty data to show something
                        setGeneratedChart({
                            data: [],
                            series: ['Value'],
                            config: { ...config }
                        });
                    }
                } catch (apiError) {
                    console.error('📈 AdvancedCharts - API call failed:', apiError);
                    console.error('📈 AdvancedCharts - Error details:', apiError instanceof Error ? apiError.message : String(apiError));
                }
            }
        } catch (error) {
            console.error('📈 AdvancedCharts - Error generating chart:', error);
        } finally {
            setIsGenerating(false);
            setShowModal(false);
        }
    };

    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6'];

    const toggleCompareItem = (item: string) => {
        setChartConfig(prev => ({
            ...prev,
            compareBy: prev.compareBy.includes(item)
                ? prev.compareBy.filter(i => i !== item)
                : [...prev.compareBy, item].slice(0, 4)
        }));
    };

    return (
        <div className="py-4">
            {/* Generated Chart Display */}
            {generatedChart ? (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4"
                >
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-700">
                                {metricLabels[generatedChart.config.yAxisMetric]} by {xAxisOptions.find(x => x.value === generatedChart.config.xAxisType)?.label}
                            </span>
                            <span className="text-xs text-gray-400 px-2 py-0.5 bg-gray-100 rounded">
                                {chartTypeOptions.find(c => c.value === generatedChart.config.chartType)?.icon}
                            </span>
                        </div>
                    </div>
                    {generatedChart.data && generatedChart.data.length > 0 ? (
                    <>
                    <div className="h-48 w-full">
                        <svg width="100%" height="100%" viewBox="0 0 320 160" preserveAspectRatio="xMidYMid meet">
                            <defs>
                                {generatedChart.series.map((s: string, i: number) => (
                                    <linearGradient key={s} id={`gradient-${i}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={colors[i % colors.length]} stopOpacity="0.4" />
                                        <stop offset="100%" stopColor={colors[i % colors.length]} stopOpacity="0.05" />
                                    </linearGradient>
                                ))}
                            </defs>

                            {/* Grid lines */}
                            {[0, 1, 2, 3].map(i => (
                                <line key={i} x1="40" y1={20 + i * 35} x2="310" y2={20 + i * 35} stroke="#e5e7eb" strokeWidth="1" />
                            ))}

                            {/* Data visualization */}
                            {generatedChart.series.map((seriesName: string, si: number) => {
                                const values = generatedChart.data.map((d: any) => d[seriesName]);
                                const maxVal = Math.max(...values) * 1.1;
                                const points = generatedChart.data.map((d: any, i: number) => ({
                                    x: 50 + (i / (generatedChart.data.length - 1)) * 250,
                                    y: 130 - (d[seriesName] / maxVal) * 100,
                                }));

                                if (generatedChart.config.chartType === 'bar') {
                                    const barWidth = 240 / generatedChart.data.length / (generatedChart.series.length + 0.5);
                                    return generatedChart.data.map((d: any, i: number) => (
                                        <motion.rect
                                            key={`${seriesName}-${i}`}
                                            x={50 + i * (260 / generatedChart.data.length) + si * barWidth}
                                            y={130 - (d[seriesName] / maxVal) * 100}
                                            width={barWidth - 2}
                                            height={(d[seriesName] / maxVal) * 100}
                                            fill={colors[si % colors.length]}
                                            rx="2"
                                            initial={{ height: 0, y: 130 }}
                                            animate={{ height: (d[seriesName] / maxVal) * 100, y: 130 - (d[seriesName] / maxVal) * 100 }}
                                            transition={{ duration: 0.5, delay: i * 0.05 }}
                                        />
                                    ));
                                }

                                const pathD = `M ${points.map((p: any) => `${p.x},${p.y}`).join(' L ')}`;
                                const areaD = `${pathD} L ${points[points.length - 1].x},130 L ${points[0].x},130 Z`;

                                return (
                                    <g key={seriesName}>
                                        {generatedChart.config.chartType === 'area' && (
                                            <motion.path
                                                d={areaD}
                                                fill={`url(#gradient-${si})`}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ duration: 0.8 }}
                                            />
                                        )}
                                        <motion.path
                                            d={pathD}
                                            fill="none"
                                            stroke={colors[si % colors.length]}
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            initial={{ pathLength: 0 }}
                                            animate={{ pathLength: 1 }}
                                            transition={{ duration: 1.2, delay: si * 0.2 }}
                                        />
                                        {points.map((p: any, i: number) => (
                                            <motion.circle
                                                key={i}
                                                cx={p.x}
                                                cy={p.y}
                                                r="4"
                                                fill="white"
                                                stroke={colors[si % colors.length]}
                                                strokeWidth="2"
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                transition={{ delay: 0.8 + i * 0.05 }}
                                            />
                                        ))}
                                    </g>
                                );
                            })}

                            {/* X-axis labels */}
                            {generatedChart.data.map((d: any, i: number) => (
                                <text
                                    key={i}
                                    x={50 + (i / (generatedChart.data.length - 1)) * 250}
                                    y="148"
                                    textAnchor="middle"
                                    className="text-[9px] fill-gray-400"
                                >
                                    {d.name.length > 8 ? d.name.substring(0, 6) + '..' : d.name}
                                </text>
                            ))}
                        </svg>
                    </div>

                    {/* Legend */}
                    {generatedChart.series.length > 1 && (
                        <div className="flex flex-wrap justify-center gap-3 mt-2">
                            {generatedChart.series.map((s: string, i: number) => (
                                <div key={s} className="flex items-center gap-1.5">
                                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors[i % colors.length] }} />
                                    <span className="text-xs text-gray-600">{s}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    </>
                    ) : (
                        <div className="text-center py-8 text-gray-400">
                            <BarChart3 size={40} className="mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No data available for the selected configuration</p>
                            <p className="text-xs mt-1">Try a different time range or metric</p>
                        </div>
                    )}
                </motion.div>
            ) : (
                <div className="text-center py-8 text-gray-400">
                    <BarChart3 size={40} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No chart generated yet</p>
                </div>
            )}

            {/* Generate Button */}
            <motion.button
                onClick={() => setShowModal(true)}
                className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-shadow"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
            >
                <Star size={18} />
                Generate Custom Chart
            </motion.button>

            {/* Modal */}
            {showModal && (
                <motion.div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => setShowModal(false)}
                >
                    <motion.div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                                    <BarChart3 size={16} className="text-white" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">Create Custom Chart</h3>
                            </div>
                            <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                                <span className="text-gray-400 text-xl">×</span>
                            </button>
                        </div>

                        <div className="p-4 space-y-5">
                            {/* Chart Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Chart Type</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {chartTypeOptions.map(opt => (
                                        <button
                                            key={opt.value}
                                            onClick={() => setChartConfig(prev => ({ ...prev, chartType: opt.value as any }))}
                                            className={`p-3 rounded-xl border-2 transition-all ${chartConfig.chartType === opt.value
                                                ? 'border-purple-500 bg-purple-50'
                                                : 'border-gray-200 hover:border-gray-300'}`}
                                        >
                                            <span className="text-2xl">{opt.icon}</span>
                                            <div className="text-xs mt-1 font-medium text-gray-600">{opt.label}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* X-Axis */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">X-Axis (Horizontal)</label>
                                <select
                                    value={chartConfig.xAxisType}
                                    onChange={e => setChartConfig(prev => ({ ...prev, xAxisType: e.target.value as any, compareBy: [] }))}
                                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                >
                                    {xAxisOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                                {chartConfig.xAxisType === 'time' && (
                                    <select
                                        value={chartConfig.xAxisGranularity}
                                        onChange={e => setChartConfig(prev => ({ ...prev, xAxisGranularity: e.target.value as any }))}
                                        className="w-full p-3 mt-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    >
                                        {granularityOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* Y-Axis */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Y-Axis (Metric)</label>
                                <select
                                    value={chartConfig.yAxisMetric}
                                    onChange={e => setChartConfig(prev => ({ ...prev, yAxisMetric: e.target.value as any }))}
                                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                >
                                    {yAxisOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Compare By (only for time-based) */}
                            {chartConfig.xAxisType === 'time' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Compare By (Optional, max 4)</label>
                                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-1">
                                        {categoryOptions.salesmen.map(salesman => (
                                            <button
                                                key={salesman}
                                                onClick={() => toggleCompareItem(salesman)}
                                                className={`px-3 py-2 text-xs rounded-lg border transition-all text-left ${chartConfig.compareBy.includes(salesman)
                                                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                                                    : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                                            >
                                                {salesman}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Time Range */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Time Range</label>
                                <div className="grid grid-cols-4 gap-2">
                                    {timeRangeOptions.map(opt => (
                                        <button
                                            key={opt.value}
                                            onClick={() => setChartConfig(prev => ({ ...prev, timeRange: opt.value as any }))}
                                            className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${chartConfig.timeRange === opt.value
                                                ? 'bg-purple-600 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-gray-100">
                            <motion.button
                                onClick={generateChartData}
                                className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <Zap size={18} />
                                Generate Chart
                            </motion.button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </div>
    );
};

const PredictiveAnalyticsVisualization: React.FC<{ isDemoMode?: boolean; data?: any }> = ({ isDemoMode = true, data: liveData }) => {
    console.log('🔮 PredictiveAnalytics - isDemoMode:', isDemoMode, 'data:', liveData);

    return (
        <div className="py-4 text-center">
            <motion.div
                className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-2xl"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
            >
                <Brain className="text-purple-600" size={32} />
                <div className="text-left">
                    <div className="text-2xl font-bold text-purple-600">{isDemoMode ? '88%' : 'N/A'}</div>
                    <div className="text-xs text-gray-600">Forecast Accuracy</div>
                </div>
            </motion.div>
            <motion.p
                className="text-sm text-gray-500 mt-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
            >
                {isDemoMode ? 'AI predicts +22% revenue next quarter' : 'Predictive analytics available in demo mode'}
            </motion.p>
        </div>
    );
};

const CustomReportsVisualization: React.FC<{ isDemoMode?: boolean; data?: any }> = ({ isDemoMode = true, data: liveData }) => {
    console.log('📊 CustomReports - isDemoMode:', isDemoMode, 'data:', liveData);

    const reports = isDemoMode
        ? ['Sales', 'Clients', 'Revenue', 'Deals', 'Team', 'Growth']
        : ['Live Sales', 'Live Clients', 'Live Revenue', 'Live Deals', 'Live Team', 'Live Growth'];

    return (
        <div className="py-4 grid grid-cols-3 gap-2">
            {reports.map((report, i) => (
                <motion.div
                    key={report}
                    className="p-2 text-center bg-gray-50 rounded-lg border border-gray-100"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    whileHover={{ backgroundColor: isDemoMode ? '#EBF4FF' : '#F0FDF4' }}
                >
                    <FileText size={16} className={`mx-auto mb-1 ${isDemoMode ? 'text-gray-400' : 'text-green-500'}`} />
                    <span className="text-xs text-gray-600">{report}</span>
                </motion.div>
            ))}
        </div>
    );
};

const DataExportVisualization: React.FC<{ isDemoMode?: boolean; data?: any }> = ({ isDemoMode = true, data: liveData }) => {
    console.log('💾 DataExport - isDemoMode:', isDemoMode, 'data:', liveData);

    const exportFormats = [
        { format: 'CSV', color: '#10B981' },
        { format: 'PDF', color: '#EF4444' },
        { format: 'Excel', color: '#3B82F6' },
    ];

    return (
        <div className="py-4 flex justify-center gap-4">
            {exportFormats.map((exp, i) => (
                <motion.div
                    key={exp.format}
                    className="flex flex-col items-center p-4 rounded-xl border-2 border-dashed"
                    style={{ borderColor: exp.color }}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.15 }}
                    whileHover={{ scale: 1.05, backgroundColor: `${exp.color}10` }}
                >
                    <Download size={24} style={{ color: exp.color }} />
                    <span className="text-sm font-medium mt-1" style={{ color: exp.color }}>
                        {exp.format}
                        {!isDemoMode && <span className="text-xs block text-gray-500">Live</span>}
                    </span>
                </motion.div>
            ))}
        </div>
    );
};

// ============= SECTION CONFIGURATION =============
const sectionConfig = {
    activity: {
        title: 'Activity & Performance',
        gradient: 'from-blue-600 to-purple-600',
        subsections: [
            { id: 'real-time-activity', title: 'Real-time Activity', icon: Zap, Component: ActivityRingsVisualization, analyticsTarget: 'salesman-overview' },
            { id: 'revenue-trends', title: 'Revenue Trends', icon: TrendingUp, Component: RevenueWaveVisualization, analyticsTarget: 'revenue-charts' },
            { id: 'team-performance', title: 'Team Performance', icon: Users, Component: TeamPerformanceVisualization, analyticsTarget: 'salesman-overview' },
            { id: 'activity-summary', title: 'Activity Summary', icon: BarChart3, Component: ActivitySummaryVisualization, analyticsTarget: 'key-metrics' },
        ],
    },
    team: {
        title: 'Team & Clients',
        gradient: 'from-green-600 to-emerald-600',
        subsections: [
            { id: 'top-performers', title: 'Top Performers', icon: Trophy, Component: TopPerformersVisualization, analyticsTarget: 'salesman-overview' },
            { id: 'client-tracking', title: 'Client Analytics', icon: Heart, Component: ClientTrackingVisualization, analyticsTarget: 'client-analytics' },
            { id: 'salesman-comparison', title: 'Performance Comparison', icon: GitCompare, Component: SalesmanComparisonVisualization, analyticsTarget: 'performance-comparison' },
            { id: 'client-acquisition', title: 'Client Acquisition', icon: UserPlus, Component: ClientAcquisitionVisualization, analyticsTarget: 'client-analytics' },
        ],
    },
    inventory: {
        title: 'Inventory & Products',
        gradient: 'from-amber-600 to-orange-600',
        subsections: [
            { id: 'stock-analytics', title: 'Stock Analytics', icon: Package, Component: StockAnalyticsVisualization, analyticsTarget: 'product-analytics' },
            { id: 'product-performance', title: 'Product Performance', icon: ShoppingCart, Component: ProductPerformanceVisualization, analyticsTarget: 'product-analytics' },
            { id: 'supplier-analytics', title: 'Supplier Analytics', icon: Truck, Component: SupplierAnalyticsVisualization, analyticsTarget: 'supplier-data' },
            { id: 'samples-tracking', title: 'Samples Tracking', icon: Beaker, Component: SamplesTrackingVisualization, analyticsTarget: 'product-analytics' },
        ],
    },
    analytics: {
        title: 'Analytics & Insights',
        gradient: 'from-purple-600 to-indigo-600',
        subsections: [
            { id: 'advanced-charts', title: 'Advanced Charts', icon: LineChart, Component: AdvancedChartsVisualization, analyticsTarget: 'advanced-analytics' },
            { id: 'predictive-analytics', title: 'Predictive Analytics', icon: Brain, Component: PredictiveAnalyticsVisualization, analyticsTarget: 'advanced-analytics' },
            { id: 'custom-reports', title: 'Custom Reports', icon: FileText, Component: CustomReportsVisualization, analyticsTarget: 'top-performers' },
            { id: 'data-export', title: 'Data Export', icon: Download, Component: DataExportVisualization, analyticsTarget: 'top-performers' },
        ],
    },
};

// ============= MAIN COMPONENT =============
const EnhancedSections: React.FC<EnhancedSectionsProps> = ({
    period,
    startDate,
    endDate,
    isDemoMode = true,
    overview,
    salesmanPerformance,
    dealsByPeriod,
    clientsByPeriod,
    revenueTrends,
    topPerformers,
    activitySummary,
    stockAnalytics,
    productAnalytics,
    supplierAnalytics,
}) => {
    const navigate = useNavigate();

    console.log('🎨 EnhancedSections - isDemoMode:', isDemoMode, 'overview:', overview);

    // Helper function to get props for each visualization component
    const getVisualizationProps = (subsectionId: string) => {
        const baseProps = { isDemoMode, period, startDate, endDate };

        switch (subsectionId) {
            case 'real-time-activity':
                return { ...baseProps, data: activitySummary };
            case 'revenue-trends':
                return { ...baseProps, data: revenueTrends };
            case 'team-performance':
                return { ...baseProps, data: salesmanPerformance };
            case 'activity-summary':
                return { ...baseProps, data: activitySummary };
            case 'top-performers':
                return { ...baseProps, data: topPerformers };
            case 'client-tracking':
                return { ...baseProps, data: clientsByPeriod };
            case 'salesman-comparison':
                return { ...baseProps, data: salesmanPerformance };
            case 'client-acquisition':
                return { ...baseProps, data: clientsByPeriod };
            case 'stock-analytics':
                return { ...baseProps, data: stockAnalytics };
            case 'product-performance':
                return { ...baseProps, data: productAnalytics };
            case 'supplier-analytics':
                return { ...baseProps, data: supplierAnalytics };
            case 'samples-tracking':
                return { ...baseProps, data: productAnalytics };
            default:
                return baseProps;
        }
    };

    return (
        <div className="space-y-16">
            {Object.entries(sectionConfig).map(([sectionKey, section]) => (
                <motion.div
                    key={sectionKey}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-8"
                >
                    {/* Section Header */}
                    <div className="text-center">
                        <h2 className={`text-4xl font-bold bg-gradient-to-r ${section.gradient} bg-clip-text text-transparent mb-2`}>
                            {section.title}
                        </h2>
                        <p className="text-gray-500">Rich visual insights at a glance</p>
                    </div>

                    {/* Enhanced Subsection Cards Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {section.subsections.map((subsection, index) => {
                            const SubIcon = subsection.icon;
                            const VisualizationComponent = subsection.Component;

                            return (
                                <motion.div
                                    key={subsection.id}
                                    id={`${subsection.id}-section`}
                                    className="scroll-mt-32 bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.1 }}
                                    whileHover={{ y: -4 }}
                                >
                                    {/* Subsection Header */}
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                                                <SubIcon size={20} className="text-gray-600" />
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-900">{subsection.title}</h3>
                                        </div>
                                        <button
                                            onClick={() => navigate(`/admin/analytics#${subsection.analyticsTarget}`)}
                                            className="flex items-center gap-1 text-sm font-medium text-blue-500 hover:text-blue-700 transition-colors"
                                        >
                                            <span>More</span>
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>

                                    {/* Rich Visualization Component */}
                                    <VisualizationComponent {...getVisualizationProps(subsection.id)} />
                                </motion.div>
                            );
                        })}
                    </div>
                </motion.div>
            ))}

            {/* CTA to Full Analytics */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 rounded-2xl p-8 shadow-xl"
            >
                <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-6">
                        <motion.div
                            animate={{ rotate: [0, 5, -5, 0], scale: [1, 1.05, 1] }}
                            transition={{ duration: 4, repeat: Infinity }}
                            className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-2xl"
                        >
                            <ChartBarIcon className="w-8 h-8 text-white" />
                        </motion.div>
                        <div>
                            <h3 className="text-2xl font-bold text-white mb-2">Explore Full Analytics</h3>
                            <p className="text-blue-200">Detailed salesman performance, comprehensive charts, and advanced metrics.</p>
                        </div>
                    </div>
                    <motion.button
                        onClick={() => navigate('/admin/analytics')}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center gap-3 px-8 py-4 bg-white text-blue-900 rounded-xl font-bold text-lg shadow-xl"
                    >
                        <span>View Full Analytics</span>
                        <ArrowRightIcon className="w-5 h-5" />
                    </motion.button>
                </div>
            </motion.div>
        </div>
    );
};

export default EnhancedSections;
